/* Self-contained (its own literals, not `var(--bottom-margin-bottom)`/
   `var(--box-shadow-props)` from whichever reading mode's own CSS module
   happens to be loaded) -- this component is shared across responsive,
   single-page replica and double-page replica, and shouldn't depend on
   which of their stylesheets is present. */
.issue-cover {
	cursor: pointer;
	position: absolute;
	height: 80px;
	/* Matches every home screen's own cover ratio (e.g. `templates/basic/
	   home/magazine/magazine.module.css`'s `.latest-issue figure img`/
	   `.issue img`, the same 9:12 used by nearly every other template's
	   home) -- this is the same cover artwork, just shown small here, so a
	   fixed pixel width cropped it differently than the reader saw on the
	   home screen a moment earlier. */
	aspect-ratio: 9 / 12;
	--cover-border: 1px;
	border: 0;
	border-radius: 4px;
	z-index: 1;
	bottom: 0;
	inset-inline-start: 0;
	margin-bottom: 20px;
	margin-inline-start: 20px;
	/* A real `<button>`, not a decorative `<div>` -- resets the UA's default
	   button chrome (padding, font inheritance, focus/appearance quirks) so
	   it still renders exactly like a plain image card. The padding is the
	   room for the frame's border (`::before`). */
	box-sizing: border-box;
	padding: var(--cover-border);
	background: transparent;
	font: inherit;
	appearance: none;
	-webkit-appearance: none;
	/* Not `overflow: hidden`: the downloaded tag sticks out above the
	   cover. The image is rounded to the inner radius instead. */
}

/* The card itself -- background, border and shadow -- drawn by a layer
   rather than the button: a button's own background and border always
   paint under its children, so the downloaded tag (behind the cover)
   would show over them. This layer paints over the tag, the image over
   this layer. */
.issue-cover::before {
	content: '';
	position: absolute;
	z-index: 0;
	inset: 0;
	box-sizing: border-box;
	border: var(--cover-border) solid var(--cf-app-color-border);
	border-radius: inherit;
	background: var(--cf-app-color-surface);
	box-shadow: 1px 1px 6px 0px rgba(0, 0, 0, 0.25);
	transition: background-color ease-out 220ms;
	pointer-events: none;
}

/* Phones and tablets (`DESKTOP_BREAKPOINT`, 1024px, and below): scrolling an
   article down slides the cover straight off the bottom edge -- no fade --
   and scrolling back up slides it back in. The distance is its own height
   plus the bottom margin and a little for the shadow, so none of it peeks
   above the edge at either size. Desktop never gets `data-hidden`'s effect.
   It stays focusable, so a keyboard focus brings it back into view. */
@media only screen and (max-width: 1024px) {
	.issue-cover {
		transition: transform ease-out 350ms;
	}

	.issue-cover[data-hidden='true'] {
		transform: translateY(calc(100% + 20px + 12px));
	}

	.issue-cover[data-hidden='true']:focus-visible {
		transform: none;
	}
}

@media only screen and (min-width: 768px) and (max-width: 1024px) {
	.issue-cover[data-hidden='true'] {
		transform: translateY(calc(100% + 35px + 24px));
	}
}

/* The downloaded tag sits above the cover, so it travels that much further
   to clear the bottom edge too. */
@media only screen and (max-width: 1024px) {
	.issue-cover[data-downloaded='true'][data-hidden='true'] {
		transform: translateY(
			calc(100% + 20px + 12px + var(--downloaded-tag-height))
		);
	}
}

@media only screen and (min-width: 768px) and (max-width: 1024px) {
	.issue-cover[data-downloaded='true'][data-hidden='true'] {
		transform: translateY(
			calc(100% + 35px + 24px + var(--downloaded-tag-height))
		);
	}
}

/* `data-motion`, not the media query: it also carries the reader's
   Settings override (see useReducedMotion, @shared/lib/hooks). */
:root[data-motion='reduce'] .issue-cover {
	transition: none;
}

/* `IssueCoverPlaceholder` while the issue loads: same look on purpose (it
   morphs into and is swapped for the real cover), only the cursor says it
   can't be pressed yet. */
.issue-cover:disabled {
	cursor: default;
}

.issue-cover img {
	/* Positioned, so it paints over the frame (`::before`). */
	position: relative;
	width: 100%;
	height: 100%;
	object-fit: cover;
	/* Long-pressing a cover opens Safari's save/copy sheet, which no native
	   reader does. */
	-webkit-touch-callout: none;
	/* The button's radius less its border, now that the button no longer
	   clips. */
	border-radius: 3px;
}

/* Downloaded issue: a tab behind the cover, as wide as it, showing above
   its top edge and reaching down behind it to three quarters of its
   height. Its negative z-index puts it under the cover's frame and image
   but, inside the button's own stacking context, still above the article.
   It rises from behind the cover when the reader opens. A label too long
   for it ends in an ellipsis. */
.issue-cover {
	--downloaded-tag-height: 16px;
}

.issue-cover .downloaded-tag {
	position: absolute;
	z-index: -1;
	top: calc(-1 * var(--downloaded-tag-height));
	left: 50%; /* rtl-ok: centred on the cover, the same in both directions */
	translate: -50% 0;
	width: 100%;
	height: calc(var(--downloaded-tag-height) + 75%);
	box-sizing: border-box;
	padding: 0 3px;
	padding-top: 1px;
	border: var(--cover-border) solid var(--cf-app-color-border);
	border-radius: 4px;
	background: var(--cf-app-color-surface);
	color: var(--cf-app-color-ink-muted);
	font-family: var(--app-title-font-family);
	font-size: 8px;
	/* The visible part, less the room between the label and the cover. */
	line-height: calc(var(--downloaded-tag-height) - 4px - var(--cover-border));
	text-align: center;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	animation: downloaded-tag-expand ease-out 350ms both;
}

@keyframes downloaded-tag-expand {
	from {
		transform: translateY(var(--downloaded-tag-height));
	}
	to {
		transform: none;
	}
}

:root[data-motion='reduce'] .issue-cover .downloaded-tag {
	animation: none;
}

/* Twice the size from tablet width up -- drawn at that size, not scaled
   up with `transform: scale(2)`: Safari rasterizes a transformed element at
   its untransformed size and stretches that bitmap, so the cover (and its
   morph snapshot) came out blurry there while Chromium re-rasterized it
   sharp. Every value is what the old `scale(2)` produced -- the same 120x160
   box at the same spot (the scale grew it from its centre, so the margins
   are the old 75px minus half the growth), with the border, radius and
   shadow doubled the way the transform doubled them. */
@media only screen and (min-width: 768px) {
	.issue-cover {
		height: 160px;
		--cover-border: 2px;
		border-radius: 8px;
		--downloaded-tag-height: 24px;
	}

	.issue-cover::before {
		box-shadow: 2px 2px 12px 0px rgba(0, 0, 0, 0.25);
		background: oklch(var(--cf-app-color-surface) / 30%);
		border-color: oklch(from var(--cf-app-color-border) l c h / 30%);
	}

	/* Opaque over the tag, which would otherwise show through. */
	.issue-cover[data-downloaded='true']::before {
		background: var(--cf-app-color-surface);
		border-color: var(--cf-app-color-border);
	}

	.issue-cover img {
		border-radius: 6px;
	}

	.issue-cover .downloaded-tag {
		padding: 0 6px;
		padding-top: 2px;
		border-radius: 8px;
		font-size: 12px;
		line-height: calc(
			var(--downloaded-tag-height) - 6px - var(--cover-border)
		);
	}
}
